import { Component, OnDestroy, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { combineLatest, Subscription } from 'rxjs'
import { filter, finalize, map, switchMap, take } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { ApplicationProviderService } from '../../services/application.service'
import { OnboardingService } from '../../services/onboarding.service'
import { CreateAccountService } from 'src/app/create-account/services/create-account.service'

@Component({
  selector: 'app-account-root',
  templateUrl: './account-root.component.html',
  styleUrls: ['./account-root.component.scss'],
  standalone: false,
})
export class AccountRootComponent implements OnInit, OnDestroy {
  private applicationSubscription: Subscription = null
  private permissionSubscription: Subscription = null
  public isAccountAdmin = false
  // Track whether we've completed the initial application check (drives template loading state)
  public initialCheckComplete = false

  constructor(
    private accountService: AccountService,
    public apService: ApplicationProviderService,
    private uiService: UiService,
    private accessLevelService: AccessLevelService,
    private router: Router,
    private onboardingService: OnboardingService,
    private createAccountService: CreateAccountService
  ) {}

  ngOnInit() {
    // Check if user is account admin
    this.permissionSubscription = this.accessLevelService
      .hasAccountPermission(AccountPermission.MANAGE_RBAC)
      .subscribe((hasManageRbac) => {
        this.isAccountAdmin = hasManageRbac
      })

    // Wait for both applications and onboarding state to load, then route accordingly.
    // Uses combineLatest so we have the full picture before making a routing decision.
    // prepareForAccountLoad() in AccountShellComponent ensures applicationsReady$ is false
    // before accountIdSource.next() fires, preventing stale ready=true from a previous load.
    this.applicationSubscription = this.accountService.accountIdStream
      .pipe(
        switchMap((accountId) => {
          return combineLatest([this.apService.applicationsReady$, this.onboardingService.state$]).pipe(
            filter(([appsReady]) => appsReady && this.onboardingService.loaded),
            take(1),
            map(() => accountId)
          )
        }),
        take(1),
        finalize(() => {
          this.applicationSubscription = null
          this.initialCheckComplete = true
        })
      )
      .subscribe((accountId: string) => {
        const apps = this.apService.loadedApplications()
        if (apps.length > 0) {
          // Navigate to the first application
          this.router.navigateByUrl(this.uiService.buildApplicationUrl(accountId, apps[0].applicationId))
        } else if (this.shouldShowWelcome(accountId)) {
          // New account flow: no apps, intro not yet dismissed, not a legacy account
          this.router.navigateByUrl(this.uiService.buildAccountWelcomeUrl(accountId))
        } else {
          // Legacy account or previously dismissed — don't show welcome again.
          // Navigate to the create app page so the user can add their first application.
          this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(accountId))
        }
      })
  }

  /**
   * Only show the welcome page for genuinely new onboarding flows.
   * Returns false for legacy accounts (no persisted onboarding keys) and
   * accounts where the intro was previously dismissed — even if the app list
   * is temporarily empty due to an API error or race condition.
   */
  private shouldShowWelcome(accountId: string): boolean {
    if (this.onboardingService.introDismissed) return false
    // Freshly-created account: bypass the seeded check even if the seeded
    // uiState write hasn't propagated to the load yet.
    if (this.createAccountService.createdAccountId === accountId) return true
    // Only seeded accounts are in the welcome flow. Legacy accounts — even ones
    // that incidentally got onboarding keys written via mark-visited — never see
    // the welcome page.
    return this.onboardingService.isSeededOnboarding
  }

  ngOnDestroy() {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
      this.applicationSubscription = null
    }
    if (subscriptionActive(this.permissionSubscription)) {
      this.permissionSubscription.unsubscribe()
      this.permissionSubscription = null
    }
  }
}
